'use client';

import { useRef } from 'react';

import { RecapAsset } from '@/api/trading/types';
import { useRecap } from '@/api/trading/useRecap';

import { TickersTapeCard } from '@/app/[lang]/price-chart/[category]/TickersTape/TickersTapeCard';

import styles from './styles.module.scss';

const tickersTapeAssetsPresets: Record<RecapAsset['category'], RecapAsset['name'][]> = {
  crypto: [
    'BTC/USD',
    'ETH/USD',
    'BNB/USD',
    'XRP/USD',
    'SOL/USD',
    'ADA/USD',
    'DOGE/USD',
    'MATIC/USD',
    'DOT/USD',
    'LTC/USD',
  ],
  forex: [
    'EUR/USD',
    'GBP/USD',
    'USD/JPY',
    'USD/CAD',
    'GBP/AUD',
    'GBP/JPY',
    'EUR/AUD',
    'CAD/JPY',
    'AUD/USD',
    'USD/CHF',
  ],
  indices: ['SP500', 'NASDAQ', 'EUR50', 'UK100', 'GER30', 'DOWJ', 'JAPAN', 'AUS200', 'HK-HSI'],
  commodities: ['GOLD', 'BRENT', 'NAT.GAS', 'SILVER', 'CRUDE', 'COPPER', 'ALUMINIUM'],
  stocks: ['ABNB', 'ADBE', 'ADSK', 'AMD', 'AMZN', 'GOOGL', 'NFLX', 'TSLA'],
};

const tickersTapeDefaultAssets = [
  'BTC/USD',
  'EUR/USD',
  'ETH/USD',
  'GBP/USD',
  'GOLD',
  'XAU/USD',
  'NASDAQ',
  'GER30',
  'USD/JPY',
  'XRP/USD',
  'USD/CAD',
  'SP500',
  'BNB/USD',
  'GBP/AUD',
  'BRENT',
  'SOL/USD',
  'ADA/USD',
];

function getAssetsNamesToRender(category?: string | null) {
  if (!category || !(category in tickersTapeAssetsPresets)) {
    return tickersTapeDefaultAssets;
  }
  return tickersTapeAssetsPresets[category as keyof typeof tickersTapeAssetsPresets];
}

export function TickersTapeClient(props: {
  markets: RecapAsset[];
  category?: RecapAsset['category'] | null;
}) {
  const { markets, error } = useRecap(undefined, props.markets);
  const rowRef = useRef<HTMLDivElement | null>(null);

  if (!markets) {
    return null;
  }

  const assetsToRender = getAssetsNamesToRender(props.category)
    .map((assetName) => markets.find((asset) => asset.name === assetName))
    .filter(Boolean) as RecapAsset[];

  const rowWidth = rowRef?.current?.children[0].clientWidth;
  const AVG_CARD_WIDTH = 260;
  const speed = (rowWidth || AVG_CARD_WIDTH * assetsToRender.length) / 60; // width / time = speed

  const row = (
    // @ts-ignore
    <div className={styles.row} style={{ '--speed': speed + 's' }}>
      {assetsToRender.map((asset, index) => (
        <TickersTapeCard key={asset.name} asset={asset} />
      ))}
    </div>
  );

  return (
    <div className={styles.tickersTape} ref={rowRef}>
      {row}
      {row}
    </div>
  );
}
